import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import StoryRing from './StoryRing';
import { StoryGroup } from '../../types/story';
import { storyService } from '../../services/api';

interface StoriesSectionProps {
  onCreateStory: () => void;
  onViewStory: (storyGroup: StoryGroup, startIndex?: number) => void;
  refreshTrigger?: number; // Add this to trigger refresh
}

const StoriesSection: React.FC<StoriesSectionProps> = ({
  onCreateStory,
  onViewStory,
  refreshTrigger
}) => {
  const [stories, setStories] = useState<StoryGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    console.log('StoriesSection useEffect triggered, refreshTrigger:', refreshTrigger);
    loadStories();
  }, [refreshTrigger]); // Reload when refreshTrigger changes

  const loadStories = async () => {
    try {
      setLoading(true);
      console.log('Loading stories...');
      const response = await storyService.getFeedStories();
      console.log('Stories response:', response.data);
      console.log('Stories response status:', (response as any).status);
      
      if ((response as any)?.success) {
        const storiesData = (response as any).data.stories || [];
        console.log('Stories loaded:', storiesData.length, 'story groups');
        console.log('Story data details:', JSON.stringify(storiesData, null, 2));
        setStories(storiesData);
        console.log('Stories state updated, current stories:', storiesData);
      } else {
        console.log('Stories response not successful:', response.data);
        console.log('Full response:', JSON.stringify(response, null, 2));
      }
    } catch (error) {
      console.error('Failed to load stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollStories = (direction: 'left' | 'right') => {
    const container = document.getElementById('stories-container');
    if (container) {
      const scrollAmount = 200;
      const newPosition = direction === 'left' 
        ? Math.max(0, scrollPosition - scrollAmount)
        : scrollPosition + scrollAmount;
      
      container.scrollTo({ left: newPosition, behavior: 'smooth' });
      setScrollPosition(newPosition);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setScrollPosition(e.currentTarget.scrollLeft);
  };

  if (loading) {
    return (
      <div className="bg-white p-4 mb-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex space-x-4 overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex flex-col items-center space-y-1 flex-shrink-0">
              <div className="w-16 h-16 rounded-full bg-gray-200 animate-pulse"></div>
              <div className="w-12 h-3 bg-gray-200 rounded animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasStories = stories.length > 0;
  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = true; // We'll assume there might be more content

  return (
    <div className="bg-white p-4 mb-4 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900">Stories</h3>
        {hasStories && (
          <div className="flex space-x-1">
            <button
              onClick={() => scrollStories('left')}
              disabled={!canScrollLeft}
              className={`p-1 rounded-full ${
                canScrollLeft 
                  ? 'text-gray-600 hover:bg-gray-100' 
                  : 'text-gray-300 cursor-not-allowed'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollStories('right')}
              className="p-1 rounded-full text-gray-600 hover:bg-gray-100"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className="relative">
        <div
          id="stories-container"
          className="flex space-x-4 overflow-x-auto scrollbar-hide pb-2"
          onScroll={handleScroll}
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {/* Add Story Button */}
          <div className="flex-shrink-0">
            <StoryRing
              isAddStory
              onClick={onCreateStory}
            />
          </div>

          {/* Story Rings */}
          {stories.map((storyGroup) => (
            <div key={storyGroup.user.id} className="flex-shrink-0">
              <StoryRing
                storyGroup={storyGroup}
                onClick={() => onViewStory(storyGroup)}
              />
            </div>
          ))}

          {/* Empty State */}
          {!hasStories && (
            <div className="flex-1 text-center py-8">
              <div className="text-gray-500">
                <p className="text-sm">No stories yet</p>
                <p className="text-xs text-gray-400 mt-1">
                  Be the first to share a story!
                </p>
              </div>
            </div>
          )}
        </div>
      </div>


    </div>
  );
};

export default StoriesSection;